import { use, useCallback, useEffect, useRef, useState } from "react";

import { ScreenSizesType, getScreenSize } from "@/theme/responsive.ts";

export type WindowSize = {
  width: number;
  height: number;
  screenSize: keyof ScreenSizesType;
};

export const awaitInitialWindowSize = new Promise<WindowSize>(resolve => {
  const interval = setInterval(() => {
    if (window.innerWidth && window.innerHeight) {
      clearInterval(interval);
      resolve({
        width: window.innerWidth,
        height: window.innerHeight,
        screenSize: getScreenSize(window.innerWidth),
      });
    }
  }, 50);
});

export const useWindowSize = (): WindowSize => {
  const initialWindowSize = use(awaitInitialWindowSize);
  const [windowSize, setWindowSize] = useState<WindowSize>(initialWindowSize);
  const updateSize = useCallback(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
      screenSize: getScreenSize(window.innerWidth),
    });
  }, []);

  const rafRef = useRef<number>(null);

  useEffect(() => {
    // Suspend if not yet measured

    const handleResize = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      // Schedule update after the current frame is finished to prevent flashing
      rafRef.current = requestAnimationFrame(updateSize);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [windowSize, updateSize]);

  return windowSize as WindowSize;
};
